/**
 * PM12CurrencyToggle — EUR/USD switch for /pro-member-v12.
 *
 * Same contract as the v11 toggle (PMPricingFaqCtaClient.tsx), scoped to the v12
 * page instead of the v11 pricing block: the choice lives in the pf_currency
 * cookie, written ONLY on a click; page load never writes it; the retired
 * localStorage key is cleared. A click swaps every [data-eur][data-usd] string on
 * the page (hero line, cards, final CTA) and tells TrustSection via CURRENCY_EVENT.
 *
 * Also owns the checkout "Change" return: ?plan=annual|monthly focuses that
 * plan's button (without scrolling past the #pricing anchor).
 *
 * r14d (2026-09-25): one pricing section again, so one toggle — the r11 cross-toggle
 * sync (listening for CURRENCY_EVENT) and the `landing` switch are gone. The toggle
 * still DISPATCHES CURRENCY_EVENT for TrustSection's followToggle block.
 */

'use client';

import { useEffect, useState } from 'react';
import { CURRENCY_EVENT } from '@/components/checkout/TrustSection';
import { clearLegacyCurrency, writeCurrencyCookie } from '@/lib/currency-cookie';
import { planFromParam, type Currency } from '@/lib/data/membership-checkout';
import { pm12Track } from './track';

export default function PM12CurrencyToggle({ initialCurrency }: { initialCurrency: Currency }) {
  const [currency, setCurrency] = useState<Currency>(initialCurrency);

  // r8 (2026-09-23): a page LOAD on #pricing (checkout's "Change" return,
  // ?plan=…#pricing, or a shared link) starts the smooth scroll before the web
  // fonts swap; the swap shortens the sections above by ~64px at 390 and the
  // scroll ends that far past the anchor. Once the fonts are in, land it again —
  // scrollIntoView honours the section's scroll-margin (pm12.css), so phones
  // land at the H2 and desktop at the section top, the same as a click.
  useEffect(() => {
    if (window.location.hash !== '#pricing') return;
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) document.getElementById('pricing')?.scrollIntoView({ block: 'start' });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    clearLegacyCurrency();
    const chosen = planFromParam(new URLSearchParams(window.location.search).get('plan'));
    if (!chosen) return;
    document
      .querySelector<HTMLAnchorElement>(`#pricing [data-tier="${chosen}"] .pm12-plan__cta`)
      ?.focus({ preventScroll: true });
  }, []);

  function choose(next: Currency) {
    writeCurrencyCookie(next);
    setCurrency(next);
    document.querySelectorAll<HTMLElement>('.pm12-page [data-eur][data-usd]').forEach((el) => {
      const text = next === 'EUR' ? el.dataset.eur : el.dataset.usd;
      if (text !== undefined) el.textContent = text;
    });
    window.dispatchEvent(new CustomEvent(CURRENCY_EVENT, { detail: next }));
    pm12Track('pm12_currency_toggle', { currency: next });
  }

  return (
    <div className="pm12-toggle" role="group" aria-label="Select currency">
      {(['EUR', 'USD'] as const).map((c) => (
        <button
          key={c}
          type="button"
          className={`pm12-toggle__btn${currency === c ? ' pm12-toggle__btn--active' : ''}`}
          aria-pressed={currency === c}
          data-currency={c}
          onClick={() => choose(c)}
        >
          {c}
        </button>
      ))}
    </div>
  );
}
